import { useContext, useMemo } from "react"
import { generatePath, matchPath, useLocation, useNavigate } from "react-router-dom"
import { PanelMenu } from "primereact/panelmenu"
import { classNames } from "primereact/utils"
import { UserContext } from "../../context/userContext"
import { normalizeRoutePath } from "../Routes/routeMatching"
import ROUTES from "../Routes/routesContants"

import baseStyles from "../../styles/SidebarPanelMenuBase.module.css"
import styles from "../../styles/Sidebar-FBT.module.css"

export default function EnterpriseLibrarySidebar() {
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()
  const location = useLocation()

  const menuModel = useMemo(() => {
    // Build enterprise profile path from shared route constants.
    const enterpriseProfilePath = generatePath(
      ROUTES.LIBRARY_ENTERPRISE_PROFILE,
      {
        idTemplate: "enterprise_profile",
        formPage: "1",
        idStamp: String(userCtx.session.user.enterprise_id_stamp || ""),
      },
    )

    // Normalize route shapes once so active checks are stable across leading/trailing slash variations.
    const normalizePath = (path) => {
      const normalizedPath = normalizeRoutePath(path)
      return normalizedPath.length > 1
        ? normalizedPath.replace(/\/+$/, "")
        : normalizedPath
    }

    const normalizedCurrentPath = normalizePath(location.pathname)

    // FBT links use section-level matching so detail routes still highlight their owning entry.
    const isPathActive = (path) => {
      const normalizedTargetPath = normalizePath(path)

      if (!normalizedTargetPath) {
        return false
      }

      if (normalizedCurrentPath === normalizedTargetPath) {
        return true
      }

      return Boolean(
        matchPath(
          { path: normalizedTargetPath, end: false },
          normalizedCurrentPath,
        ),
      )
    }

    // Utility for combining optional item classes without trailing spaces.
    const withClass = (...classNames) =>
      classNames.filter(Boolean).join(" ") || undefined

    // Build a leaf-only PanelMenu item (no submenus in this menu currently).
    const buildLeaf = ({ label, path, sectionStart = false }) => {
      const isActive = isPathActive(path)

      return {
        label,
        // `section-start` is a plain global marker used by CSS to draw top separators.
        className: withClass(
          sectionStart ? "section-start" : "",
          isActive ? baseStyles.menuLinkActiveItem : "",
        ),
        // Use command navigation so behavior stays consistent with other PanelMenu sidebars.
        command: () => {
          navigate(path)
        },
      }
    }

    // Preserve legacy grouping/order while rendering via PanelMenu.
    return [
      buildLeaf({ label: "Enterprise Profile", path: enterpriseProfilePath }),
      buildLeaf({ label: "Fields", path: "/enterprise/library", sectionStart: true }),
      buildLeaf({ label: "Blocks", path: "/library/blocks" }),
      buildLeaf({ label: "Templates", path: "/library/templates" }),
      buildLeaf({ label: "Assessments", path: "/library/assessments", sectionStart: true }),
      buildLeaf({ label: "Coordination Boards", path: "/library/coordination-boards" }),
      buildLeaf({ label: "Document Templates", path: "/library/documents" }),
      buildLeaf({ label: "GPRA", path: "/library/gpra" }),
      buildLeaf({ label: "Housing", path: "/library/housing" }),
      buildLeaf({ label: "Kiosk", path: "/library/kiosk" }),
      buildLeaf({ label: "Telerecovery", path: "/library/telerecovery" }),
    ]
  }, [location.pathname, navigate, userCtx.session.user.enterprise_id_stamp])

  return (
    <div className={classNames(baseStyles.menuRoot, styles.menuRoot)}>
      {/* Remount on route change to keep active state in sync with the current pathname. */}
      <PanelMenu
        key={`enterprise-library-menu-${location.pathname}`}
        model={menuModel}
        multiple={false}
      />
    </div>
  )
}